import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { PhotoEffectId } from '@/lib/photo-effects/types/photo-effect-id'
import type { PhotoEffectRuntimeCatalog } from '@/lib/photo-effects/types/photo-effect-runtime-catalog'
import { buildFallbackPhotoEffectRuntimeCatalog } from '@/lib/photo-effects/photo-effect-catalog'
import {
  buildPezTransformsFromPhotoEffectsState,
  photoEffectsStateFromPezTransforms,
} from '@/lib/photo-effects/photo-effect-pez-transforms'
import type { PhotoEffectsState } from '@/lib/photo-effects/types/photo-effects-state'
import {
  clearAllPhotoEffects,
  countActivePhotoEffects,
  createDefaultPhotoEffectsState,
  orderPhotoEffectsForDisplay,
  withPhotoEffectValue,
  withToggledPhotoEffect,
} from '@/lib/photo-effects/photo-effects-state'
import { resolvePlacedPhotoIdentity } from '@/lib/photos/fill-shape-with-photo'
import { photosService } from '@/api/services/photos.service'
import { queryKeys } from '@/api/query-keys'
import { usePhotoEffectsCatalog } from '@/api/hooks/use-photo-effects-catalog'
import { usePhotoPez } from '@/api/hooks/use-photo-pez'
import {
  type PhotoEffectHistoryGesture,
  buildPhotoEffectHistoryTxnId,
} from '@/lib/photo-effects/photo-effect-apply-history'

const PEZ_APPLY_DEBOUNCE_MS = 400

/**
 * One Photo Effects editing session for a placed photo: the effect values and activation order,
 * restoring them from the photo's saved PEZ, merging the live catalog in, and the debounced apply
 * that creates a new PEZ and hands it to `onPezApplied` (flushed if the popover closes mid-debounce).
 * Each user gesture gets a history transaction id so a slider drag undoes as one step.
 */
export function usePhotoEffectsSession({
  open,
  shape,
  onPezApplied,
}: {
  open: boolean
  shape: ShapeData
  onPezApplied?: (args: {
    shapeId: string
    pezId: string
    pezUrl: string
    historyTxnId?: string
  }) => void
}) {
  const queryClient = useQueryClient()

  const catalogQuery = usePhotoEffectsCatalog(open)
  const catalog: PhotoEffectRuntimeCatalog = useMemo(
    () => catalogQuery.data ?? buildFallbackPhotoEffectRuntimeCatalog(),
    [catalogQuery.data]
  )

  const candidId = resolvePlacedPhotoIdentity(shape)?.imageId ?? null
  const pezId = shape.photoFill?.pezId?.trim() ? shape.photoFill.pezId.trim() : null
  const pezQuery = usePhotoPez(pezId, open)

  const [effectsState, setEffectsState] = useState<PhotoEffectsState>(() =>
    createDefaultPhotoEffectsState(catalog.definitions)
  )
  const [activationOrder, setActivationOrder] = useState<PhotoEffectId[]>([])
  const [applyError, setApplyError] = useState<string | null>(null)
  const [isApplying, setIsApplying] = useState(false)

  const skipApplyRef = useRef(true)
  const userEditedRef = useRef(false)
  const applyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const applyGenerationRef = useRef(0)
  const hydratedRef = useRef<string | null>(null)
  const historySessionRef = useRef(0)
  const historyGestureSeqRef = useRef(0)
  const lastHistoryGestureRef = useRef<PhotoEffectHistoryGesture | null>(null)
  const pendingHistoryTxnIdRef = useRef<string | undefined>(undefined)

  const effectsStateRef = useRef(effectsState)
  const activationOrderRef = useRef(activationOrder)
  const catalogRef = useRef(catalog)

  effectsStateRef.current = effectsState
  activationOrderRef.current = activationOrder
  catalogRef.current = catalog

  const bumpHistoryGesture = useCallback(
    (gesture: PhotoEffectHistoryGesture) => {
      if (gesture === 'slider') {
        if (lastHistoryGestureRef.current !== 'slider') {
          historyGestureSeqRef.current += 1
        }
      } else {
        historyGestureSeqRef.current += 1
      }

      lastHistoryGestureRef.current = gesture
      pendingHistoryTxnIdRef.current = buildPhotoEffectHistoryTxnId(
        shape.id,
        historySessionRef.current,
        gesture,
        historyGestureSeqRef.current
      )
    },
    [shape.id]
  )

  const runPezApply = useCallback(
    async (generation: number) => {
      if (!candidId || !onPezApplied) return

      setIsApplying(true)
      setApplyError(null)

      try {
        const pezTransforms = buildPezTransformsFromPhotoEffectsState(
          effectsStateRef.current,
          catalogRef.current,
          activationOrderRef.current
        )

        const result = await photosService.createPhotoPez({ candidId, pezTransforms })

        if (generation !== applyGenerationRef.current) return

        onPezApplied({
          shapeId: shape.id,
          pezId: result.pezId,
          pezUrl: result.pezUrl,
          historyTxnId: pendingHistoryTxnIdRef.current,
        })

        void queryClient.invalidateQueries({ queryKey: queryKeys.photos.pez(result.pezId) })
      } catch (error) {
        if (generation !== applyGenerationRef.current) return

        const message = error instanceof Error ? error.message : 'Failed to update preview'

        setApplyError(message)
        toast.error(message)
      } finally {
        if (generation === applyGenerationRef.current) {
          setIsApplying(false)
        }
      }
    },
    [candidId, onPezApplied, queryClient, shape.id]
  )

  const activeCount = countActivePhotoEffects(effectsState, catalog.definitions)
  const displayRows = orderPhotoEffectsForDisplay(
    ['binary', 'symmetric', 'percent'],
    catalog.definitions
  )

  const isRestoring = open && !!pezId && pezQuery.isLoading
  const catalogWarning = catalogQuery.isError && !catalog.fromApi

  React.useEffect(() => {
    if (!open) {
      setApplyError(null)
      hydratedRef.current = null
      skipApplyRef.current = true
      userEditedRef.current = false

      if (applyTimerRef.current) {
        clearTimeout(applyTimerRef.current)
        applyTimerRef.current = null
      }

      return
    }

    skipApplyRef.current = true

    if (open) {
      historySessionRef.current += 1
      historyGestureSeqRef.current = 0
      lastHistoryGestureRef.current = null
      pendingHistoryTxnIdRef.current = undefined
    }
  }, [open, shape.id])

  React.useEffect(() => {
    if (!open) return

    hydratedRef.current = null
    skipApplyRef.current = true
    userEditedRef.current = false

    if (pezId) {
      // Step 11 — wait for GET /photos/pez to hydrate; avoid flashing "Clear all (0)".
      return
    }

    setEffectsState(createDefaultPhotoEffectsState(catalog.definitions))
    setActivationOrder([])
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when target photo or pez row changes
  }, [open, shape.id, pezId])

  React.useEffect(() => {
    if (!open || !catalog.fromApi) return

    setEffectsState((prev) => {
      const next = createDefaultPhotoEffectsState(catalog.definitions)

      for (const def of catalog.definitions) {
        const prevEntry = prev[def.id]

        if (!prevEntry) continue

        if (prevEntry.active || userEditedRef.current) {
          next[def.id] = prevEntry
        }
      }

      return next
    })
    skipApplyRef.current = true
  }, [open, catalog.fromApi, catalogQuery.dataUpdatedAt, catalog.definitions])

  React.useEffect(() => {
    if (!open || !pezId) return

    if (pezQuery.isError) {
      setEffectsState(createDefaultPhotoEffectsState(catalog.definitions))
      setActivationOrder([])

      return
    }

    if (!pezQuery.data) return

    const hydrateKey = `${shape.id}:${pezId}:${pezQuery.dataUpdatedAt}`

    if (hydratedRef.current === hydrateKey) return

    const restored = photoEffectsStateFromPezTransforms(pezQuery.data.pezTransforms, catalog)

    hydratedRef.current = hydrateKey
    skipApplyRef.current = true
    userEditedRef.current = false
    setEffectsState(restored.state)
    setActivationOrder(restored.activationOrder)
  }, [open, pezId, pezQuery.data, pezQuery.dataUpdatedAt, pezQuery.isError, shape.id, catalog])

  React.useEffect(() => {
    if (!open || !candidId || !onPezApplied) return

    if (!userEditedRef.current) {
      return
    }

    if (applyTimerRef.current) clearTimeout(applyTimerRef.current)

    const generation = applyGenerationRef.current + 1

    applyGenerationRef.current = generation

    applyTimerRef.current = setTimeout(() => {
      void runPezApply(generation)
    }, PEZ_APPLY_DEBOUNCE_MS)

    return () => {
      if (applyTimerRef.current) {
        clearTimeout(applyTimerRef.current)
        applyTimerRef.current = null
      }
    }
  }, [open, candidId, onPezApplied, shape.id, effectsState, activationOrder, catalog, runPezApply])

  React.useEffect(() => {
    if (!open) return

    return () => {
      if (!userEditedRef.current || !candidId || !onPezApplied) return

      if (applyTimerRef.current) {
        clearTimeout(applyTimerRef.current)
        applyTimerRef.current = null
        void runPezApply(applyGenerationRef.current)
      }
    }
  }, [open, candidId, onPezApplied, runPezApply])

  const handleToggle = (id: PhotoEffectId) => {
    userEditedRef.current = true
    skipApplyRef.current = false
    bumpHistoryGesture('toggle')
    const next = withToggledPhotoEffect(effectsState, activationOrder, id)

    setEffectsState(next.state)
    setActivationOrder(next.activationOrder)
  }

  const handleValueChange = (id: PhotoEffectId, value: number) => {
    userEditedRef.current = true
    skipApplyRef.current = false
    bumpHistoryGesture('slider')
    setEffectsState((prev) => withPhotoEffectValue(prev, id, value, catalog.definitions))
  }

  const handleClearAll = () => {
    userEditedRef.current = true
    skipApplyRef.current = false
    bumpHistoryGesture('clear')
    setEffectsState((prev) => clearAllPhotoEffects(prev, catalog.definitions))
    setActivationOrder([])
  }

  return {
    catalogQuery,
    pezQuery,
    effectsState,
    activeCount,
    displayRows,
    isRestoring,
    catalogWarning,
    applyError,
    isApplying,
    handleToggle,
    handleValueChange,
    handleClearAll,
  }
}
